import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";
import { IS_PROD, IS_DEV } from "@/config/env";
import { isLazyLoadError, shouldAutoReloadLazyError } from "@/lib/lazyWithRetry";

const root = ReactDOM.createRoot(document.getElementById("root"));
// React.StrictMode intentionally removed: Firebase Auth (both signInWithPopup and
// signInWithRedirect) is incompatible with StrictMode's double-mount behaviour.
root.render(<App />);

function hardBustReload() {
  const url = new URL(window.location.href);
  url.searchParams.set("_cb", String(Date.now()));
  window.location.replace(url.toString());
}

window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault?.();
  if (shouldAutoReloadLazyError()) {
    hardBustReload();
  }
});

window.addEventListener("unhandledrejection", (event) => {
  const reason = event.reason;
  console.error("[unhandledrejection]", reason);
  if (isLazyLoadError(reason) && shouldAutoReloadLazyError()) {
    event.preventDefault?.();
    hardBustReload();
  }
});

// One-shot: wipe stale SW/caches from the broken radix-chunk deploy so phones
// stuck on the blank spinner recover on first successful load of this build.
const CACHE_BUST_KEY = "tymlogr-sw-bust-2026-11-03-spa-cache";
async function bustStaleClientCaches() {
  if (typeof window === "undefined" || !IS_PROD) return false;
  try {
    if (localStorage.getItem(CACHE_BUST_KEY) === "1") return false;
    localStorage.setItem(CACHE_BUST_KEY, "1");
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    window.location.reload();
    return true;
  } catch {
    return false;
  }
}

if ("serviceWorker" in navigator && IS_PROD) {
  window.addEventListener("load", () => {
    bustStaleClientCaches().then((reloading) => {
      if (reloading) return;

      navigator.serviceWorker
        .register("/sw.js")
        .then((reg) => {
          console.log("[SW] registered:", reg.scope);
        })
        .catch((err) => {
          console.warn("[SW] registration failed:", err);
        });

      let swRefreshing = false;
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (!swRefreshing) {
          swRefreshing = true;
          window.location.reload();
        }
      });
    });
  });
}

function reportWebVitals(metric) {
  if (IS_DEV) {
    console.log(`[CWV] ${metric.name}:`, Math.round(metric.value), metric.rating);
  }
}

import("web-vitals")
  .then(({ onCLS, onFCP, onFID, onLCP, onTTFB, onINP }) => {
    onCLS(reportWebVitals);
    onFCP(reportWebVitals);
    onFID?.(reportWebVitals);
    onLCP(reportWebVitals);
    onTTFB(reportWebVitals);
    onINP?.(reportWebVitals);
  })
  .catch(() => {});
